// Commandes: every order across agencies, filtered by where it stands, with
// its journey at a glance. A row opens the order's drawer.
import { useMemo, useState } from 'react';
import { S } from '../lib/strings';
import { dayLabel, discColor, hhmm, initials, xaf, dec1 } from '../lib/format';
import { amount, matchesQuery, ORDF, ordMatch, statusPill, stepIdx, steps, isDone, isCancelled, type OrdFilter } from '../lib/orders';
import type { OpsOrder } from '../lib/types';
import { BrandCyl } from '../components/Bottles';
import { Ic } from '../components/Icon';

export function StPill({ o }: { o: Pick<OpsOrder, 'status'> }) {
  const [s, c] = statusPill(o.status);
  return <span className={'st ' + c}>{s}</span>;
}

export function JourneyMini({ o }: { o: OpsOrder }) {
  const S_ = steps(o), c = stepIdx(o), done = isDone(o.status), bad = o.status === 'failed_first_install' || isCancelled(o.status);
  return (
    <>
      <div className="jm" aria-hidden="true">
        {S_.map((s, i) => (
          <span key={s.key} style={{ display: 'contents' }}>
            {i > 0 && <s className={done ? 'ok' : i <= c ? 'd' : ''} />}
            <i className={done ? 'ok' : bad && i === c ? 'x' : i < c ? 'd' : i === c ? 'c' : ''} />
          </span>
        ))}
      </div>
      <div className="jml">{bad && o.status === 'failed_first_install' ? S.steps.failed : S_[Math.min(c, S_.length - 1)].label}</div>
    </>
  );
}

const isToday = (iso: string) => dayLabel(iso) === dayLabel(new Date());

export function Orders({ orders, openOrder }: { orders: OpsOrder[]; openOrder(id: string): void }) {
  const [f, setF] = useState<OrdFilter>('all');
  const [q, setQ] = useState('');
  const rows = useMemo(() => orders.filter((o) => ordMatch(o, f) && matchesQuery(o, q)), [orders, f, q]);
  return (
    <section className="view on" aria-label={S.tabs.ord}>
      <div className="ph"><div><h1>{S.ord.title}</h1><p>{S.ord.sub}</p></div></div>
      <div className="card">
        <div className="fbar">
          {ORDF.map(([k, c]) => (
            <button key={k} className="fchip" aria-pressed={f === k} onClick={() => setF(k)}>
              {c && <span className="d" style={{ background: c }} />}{S.ord.filters[k]}<span className="c">{orders.filter((o) => ordMatch(o, k)).length}</span>
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <label className="search"><Ic n="search" s="xs" /><span className="sr">{S.search}</span>
            <input placeholder={S.ord.find} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
        </div>
        <div className="tblwrap">
          <table className="tbl cards ordc">
            <thead><tr>{S.ord.cols.map((c, i) => <th key={c} className={i === 5 ? 'r' : undefined}>{c}</th>)}</tr></thead>
            <tbody>
              {rows.length ? rows.map((o) => {
                const amt = amount(o), inst = o.order_type === 'first_install';
                return (
                  <tr key={o.id} tabIndex={0} onClick={() => openOrder(o.id)} onKeyDown={(e) => { if (e.key === 'Enter') openOrder(o.id); }}>
                    <td><span className="mono" style={{ fontWeight: 600, color: 'var(--ink)' }}>{o.ref}</span>
                      <div className="muted" style={{ fontSize: 12 }}>{isToday(o.created_at) ? '' : dayLabel(o.created_at) + ' '}{hhmm(o.created_at)}</div></td>
                    <td><div className="who"><span className="ini" style={{ background: discColor(o.customer_id) }}>{initials(o.customer.name)}</span>
                      <div style={{ minWidth: 0 }}><b>{o.customer.name}</b><span>{o.customer.quartier} · {o.agency.name}</span></div></div></td>
                    <td className="hm"><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {o.marketer && <BrandCyl color={o.marketer.brand_color} h={28} />}
                      <div><b style={{ fontSize: 13, fontWeight: 600 }}>{o.marketer?.name ?? '—'}</b><div className="muted" style={{ fontSize: 12 }}>{dec1(o.size_kg)} kg{inst ? S.ord.kit : ''}</div></div>
                    </div></td>
                    <td><JourneyMini o={o} /></td>
                    <td>{o.rider ? (
                      <div className="who"><span className="ini" style={{ background: discColor(o.rider.id), width: 28, height: 28, fontSize: 11 }}>{initials(o.rider.name)}</span><b style={{ fontWeight: 500 }}>{o.rider.name}</b></div>
                    ) : o.status === 'placed' ? <span className="st violet nodot">{S.ord.unassigned}</span> : <span className="muted">—</span>}</td>
                    <td className="r num" style={{ fontWeight: 600 }}>{xaf(amt.total)}<div className="muted" style={{ fontSize: 12, fontWeight: 400 }}>{inst ? S.ord.kitCash : S.ord.cash}</div></td>
                    <td><StPill o={o} /></td>
                  </tr>
                );
              }) : <tr><td colSpan={7}><div className="empty"><b>{S.ord.none}</b>{S.ord.noneSub}</div></td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
